
import BCXCard from '@/components/molecules/BCXCard.vue';
import useI18n from '@/mixins/useI18n';
import useUser from '@/mixins/useUser';
import { UserRoles } from '@/models/User';
import useInfoPopupState from '@/state/infoPopupState';
import useSettings from '@/state/settingsState';
import LatestBlogs from '@/views/home/components/DashboardLatestBlogs.vue';
import NewMembers from '@/views/home/components/DashboardNewMembers.vue';
import DashboardPartnerProgramCard from '@/views/home/components/DashboardPartnerProgramCard.vue';
import DashboardPartnerProgramCardSkeleton from '@/views/home/components/DashboardPartnerProgramCardSkeleton.vue';
import SubscriptionsFeed from '@/views/home/components/DashboardSubscriptionsFeed.vue';
import UserProfileStatus from '@/views/home/components/DashboardUserProfileStatus.vue';
import { LatestUserDashboard } from '@/views/home/models/Dashboard';
import {
  getDashboardBlogMode,
  shouldShowPartnerProgramCard,
} from '@/views/home/utils/dashboardPartnerProgram';
import {
  computed, ComputedRef, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch
} from 'vue';
import { useStore } from 'vue2-helpers/vuex';

export default defineComponent({
  name: 'HomeDashboard',
  components: {
    BCXCard,
    SubscriptionsFeed,
    LatestBlogs,
    NewMembers,
    UserProfileStatus,
    DashboardPartnerProgramCard,
    DashboardPartnerProgramCardSkeleton,
  },
  setup() {
    const { t } = useI18n();
    const store = useStore();

    const { isFreelancer, hasRole } = useUser('self');
    const { showInfoPopupIfUnread } = useInfoPopupState();
    const { loadSettings, settings, areSettingsLoaded } = useSettings();

    onMounted(() => {
      nextTick(() => {
        showInfoPopupIfUnread({
          component: 'InfoPopup_Cheatsheet',
          context: 'cheatsheet',
          value: '1',
          visibleForRoles: {
            FREELANCER: true,
          },
        });
      });
    });

    const subscriptionFeedNewMembers: ComputedRef<LatestUserDashboard[]> = computed(() => store.getters['dashboard/getDashboardUsersList'] || []);
    const showUserProfileStatusCard = computed(
      () => store.state.dashboard.userProfileStatusLoaded
        && (store.state.dashboard.userProfileStatus.missingSummary || store.state.dashboard.userProfileStatus.missingImage),
    );

    const showArticlesCard = ref(true);
    const hideArticlesCard = () => {
      showArticlesCard.value = false;
    };
    const showInfoCard = ref(true);
    const hideInfoCard = () => {
      showInfoCard.value = false;
    };
    const showPartnerProgramCardSkeleton = ref(false);
    let affiliateDecisionTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearAffiliateDecisionTimeout = () => {
      if (!affiliateDecisionTimeout) return;
      clearTimeout(affiliateDecisionTimeout);
      affiliateDecisionTimeout = null;
    };

    watch(isFreelancer, (is) => {
      clearAffiliateDecisionTimeout();
      showPartnerProgramCardSkeleton.value = false;

      if (!is || areSettingsLoaded.value) return;

      affiliateDecisionTimeout = setTimeout(() => {
        if (!areSettingsLoaded.value) showPartnerProgramCardSkeleton.value = true;
        affiliateDecisionTimeout = null;
      }, 120);

      loadSettings();
    }, {
      immediate: true
    });

    watch(areSettingsLoaded, (isLoaded) => {
      if (!isLoaded) return;
      clearAffiliateDecisionTimeout();
      showPartnerProgramCardSkeleton.value = false;
    });

    onBeforeUnmount(() => {
      clearAffiliateDecisionTimeout();
    });

    const hasAffiliateConsent = computed(() => {
      if (!settings.value) return null;
      return !!settings.value.consentObjectSettings?.affiliateContract;
    });

    const shouldRenderPartnerProgramCard = computed(() => shouldShowPartnerProgramCard({
      isFreelancer: isFreelancer.value,
      hasAffiliateAccess: hasRole(UserRoles.AFFILIATE_USER),
      hasAffiliateConsent: areSettingsLoaded.value ? hasAffiliateConsent.value : null,
    }));

    const shouldRenderInfoCard = computed(() => {
      if (!showInfoCard.value) return false;
      if (!isFreelancer.value) return true;
      if (!areSettingsLoaded.value) return false;
      return !shouldRenderPartnerProgramCard.value;
    });

    const shouldRenderPartnerProgramCardSkeleton = computed(
      () => isFreelancer.value && !areSettingsLoaded.value && showPartnerProgramCardSkeleton.value,
    );
    const largeBlogMode = computed(() => getDashboardBlogMode(shouldRenderPartnerProgramCard.value));

    return {
      largeBlogMode,
      subscriptionFeedNewMembers,
      showUserProfileStatusCard,
      showArticlesCard,
      hideArticlesCard,
      hideInfoCard,
      shouldRenderInfoCard,
      shouldRenderPartnerProgramCard,
      shouldRenderPartnerProgramCardSkeleton,
      t
    };
  }
});
